// =========================================================================
// HM-2 — an event series breaks over a TRAINING pause, never an exercise one.
// =========================================================================
// `freshness.ts` asks "has this series stopped arriving at its own rhythm?".
// That is the right question for a continuously-sampled quantity: glucose
// exists between two readings, so a hole there is ignorance about something
// real. It is the wrong question for DENSITY, which every session produces:
// an athlete who trains steadily but often forgets to close the session has a
// sparse density series and no silence at all, so its own cadence would call
// him stale while he is in the gym.
//
// ⛔ 2026-09-16 — the e1RM panel NO LONGER uses this, by Antenore's decision
// ("Spezzate"). For a PER-EXERCISE metric the pause IS the information: a
// lunge you rarely train should read as rarely trained, and the broken line
// is how you see it next to its alternatives (PG-24). This primitive is for
// metrics the SESSION produces, not the exercise.
//
// So the rhythm that decides is the TRAINING calendar's, not the metric's.
// The threshold is still DERIVED (`staleAfterMs`), never declared: the
// athlete's own habit says what counts as a layoff, and an unknowable habit
// makes no accusation at all.
//
// Decided by Antenore on 2026-08-11 ("strada b"), see
// `next/measurement-contract.md#hm-2`.

import { staleAfterMs } from "./freshness.js";
import type { TimedSessionResult } from "./progressTypes.js";

/** For each point, should the path break BEFORE it? Index 0 is always false
 *  (nothing precedes it). Both inputs ascending, in epoch ms.
 *
 *  `trainingAtMs` is every session the athlete logged — not just the ones
 *  that produced a point in this series. That difference IS the rule. */
export function trainingLayoffBreaks(
  pointAtMsSorted: readonly number[],
  trainingAtMsSorted: readonly number[],
): boolean[] {
  const breaks = pointAtMsSorted.map(() => false);
  const threshold = staleAfterMs(trainingAtMsSorted);
  // Too few sessions to know this athlete's habit ⇒ never invent a layoff.
  if (threshold === null) return breaks;

  // The calendar is a WINDOW, not the whole history: the panels fetch a capped
  // number of sessions. Anything older than its first entry is unknown, not
  // absent — and a layoff we cannot see is not a layoff we may assert
  // (Antenore, 2026-09-16). Fails toward the whole line, never toward an
  // invented pause.
  const calendarStart = trainingAtMsSorted[0];
  if (calendarStart === undefined) return breaks;

  for (let i = 1; i < pointAtMsSorted.length; i++) {
    const from = pointAtMsSorted[i - 1]!;
    const to = pointAtMsSorted[i]!;
    // This pair straddles the edge of what we can see: no claim.
    if (from < calendarStart) continue;
    // Walk the sessions that fall between the two points: the line survives
    // as long as the athlete kept showing up, whatever they trained.
    let prev = from;
    let broken = false;
    for (const at of trainingAtMsSorted) {
      if (at <= from) continue;
      if (at >= to) break;
      if (at - prev > threshold) {
        broken = true;
        break;
      }
      prev = at;
    }
    breaks[i] = broken || to - prev > threshold;
  }
  return breaks;
}

/** The calendar `trainingLayoffBreaks` judges against: every session the
 *  athlete logged, ascending. ONE definition of "training" on purpose — two
 *  panels hand-rolling this would eventually disagree about whether a
 *  cardio-only or deload session counts, and disagree silently. */
export function trainingCalendarMs(
  results: readonly TimedSessionResult[] | null,
): number[] | undefined {
  if (results === null) return undefined;
  return results.map((s) => s.startedAtMs).sort((a, b) => a - b);
}
